import { ReactNode } from 'react';
import Image from '@next/image';
import classNames from 'classnames';

import { Category } from 'types/Category';
import { Classes } from 'types/Classes';
import { Description as DescriptionType } from 'types/Description';
import Typography from '@components/atoms/Typography';
import ShowMoreContainer from '@components/ShowMoreContainer';
import Section from '@components/Section';
import Description, { getPlainTextContent } from '@templates/Description';
import { useIntl } from 'utils/intl';

import classes from './CategoryLevelHeroSection.module.scss';
import messages from './CategoryLevelHeroSection.messages';

const showMoreButtonMinContentLength = 2000;

type Props = {
  data: Pick<Category, 'name' | 'imageFile'>;
  description?: Nullable<DescriptionType>;
  classes?: Classes<'box'>;
  additionalContent?: ReactNode;
};

const CategoryLevelHeroSection = ({
  data: { name, imageFile },
  description,
  classes: customClasses,
  additionalContent,
}: Props) => {
  const intl = useIntl();

  const plainDescription = description ? getPlainTextContent(description) : '';

  return (
    <>
      {(description || additionalContent) && (
        <div>
          {description && (
            <div className={classNames(classes.box, customClasses?.box, { [classes.withShadow]: additionalContent })}>
              <ShowMoreContainer
                classes={{ content: classes.boxContent }}
                containerHeight="300px"
                forceShowAll={plainDescription.length <= showMoreButtonMinContentLength}
              >
                <Section title={name} classes={{ root: classes.section }}>
                  {imageFile && (
                    <div className={classes.imageWrapper}>
                      <Image
                        layout="fill"
                        objectFit="contain"
                        alt={intl.formatMessage(messages.bannerPhoto)}
                        resolveConfig={{
                          preset: 'category_banner',
                          extension: imageFile?.extension,
                          name: imageFile?.basename,
                          productName: name,
                        }}
                        hasLazyLoad={false}
                        hasPreload
                      />
                    </div>
                  )}
                  <Typography variant="body2" renderAs="div">
                    <Description data={description} renderFakeHeaders />
                  </Typography>
                </Section>
              </ShowMoreContainer>
            </div>
          )}
          {additionalContent}
        </div>
      )}
    </>
  );
};

export default CategoryLevelHeroSection;
